<script setup>
import { reactive, computed, watch, getCurrentInstance } from 'vue';
import { Countries } from '/src/app/features/shared/libraries/countries.js';

const { proxy } = getCurrentInstance();
const { $bus, $constants, $stores } = proxy;

const sourceData = computed(() => $stores.appData?.state?.data?.contacts || {});

// Get country codes from source data keys
const countryCodes = computed(() =>
  Object.keys(sourceData.value).filter((code) => sourceData.value[code])
);

const form = reactive({});

// Initialize form for each country
watch(
  sourceData,
  (data) => {
    if (!data) return;
    Object.keys(data).forEach((code) => {
      if (data[code] && !form[code]) {
        form[code] = {
          whatsappPhoneNumber: '',
          whatsappPhoneNumberComplaint: '',
          whatsappLink: '',
          whatsappChatLink: '',
          telegramLink: ''
        };
      }
      if (data[code] && form[code]) {
        Object.assign(form[code], {
          whatsappPhoneNumber: data[code].whatsappPhoneNumber || '',
          whatsappPhoneNumberComplaint:
            data[code].whatsappPhoneNumberComplaint || '',
          whatsappLink: data[code].whatsappLink || '',
          whatsappChatLink: data[code].whatsappChatLink || '',
          telegramLink: data[code].telegramLink || ''
        });
      }
    });
  },
  { immediate: true, deep: true }
);

const hasChanges = computed(() => {
  return countryCodes.value.some((code) =>
    Object.keys(form[code] || {}).some(
      (key) => form[code]?.[key] !== sourceData.value?.[code]?.[key]
    )
  );
});

const countryHasChanges = (code) =>
  Object.keys(form[code] || {}).some(
    (key) => form[code]?.[key] !== sourceData.value?.[code]?.[key]
  );

function save() {
  if (!hasChanges.value) return;

  const payload = { contacts: {} };

  countryCodes.value.forEach((code) => {
    payload.contacts[code] = {};
    Object.keys(form[code] || {}).forEach((key) => {
      if (form[code][key] !== sourceData.value?.[code]?.[key]) {
        payload.contacts[code][key] = form[code][key];
      }
    });
  });

  $bus.emit($constants.EVENTS.APP_DATA.UPDATE_CONTACTS, payload);
}

function reset() {
  countryCodes.value.forEach((code) => {
    if (sourceData.value?.[code] && form[code]) {
      Object.assign(form[code], sourceData.value[code]);
    }
  });
}

function resetCountry(code) {
  if (sourceData.value?.[code] && form[code]) {
    Object.assign(form[code], sourceData.value[code]);
  }
}
</script>

<template>
  <div class="space-y-6">
    <!-- HEADER -->
    <div class="flex justify-between items-center">
      <h2 class="text-xl font-semibold">Contacts par pays</h2>
      <div class="flex gap-3" v-show="hasChanges">
        <button
          class="px-6 py-2 rounded-xl bg-gray-300 text-gray-700"
          @click="reset"
        >
          Annuler tout
        </button>
        <button
          class="px-6 py-2 rounded-xl bg-primary-600 text-white"
          @click="save"
        >
          Sauvegarder
        </button>
      </div>
    </div>

    <!-- COUNTRIES -->
    <div
      v-for="code in countryCodes"
      :key="code"
      class="bg-slate-100 rounded-3xl p-6"
    >
      <div
        class="flex justify-between items-center mb-4 pb-3 border-b border-gray-300"
      >
        <h3 class="text-lg font-semibold">
          {{ Countries.find(code)?.countryName || code }} ({{ code }})
        </h3>
        <button
          v-if="countryHasChanges(code)"
          class="text-sm px-4 py-1 rounded-lg bg-gray-200 text-gray-600 hover:bg-gray-300"
          @click="resetCountry(code)"
        >
          Annuler
        </button>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label class="block mb-1 text-sm font-medium">Numéro WhatsApp</label>
          <input
            class="inputComponent"
            v-model="form[code].whatsappPhoneNumber"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm font-medium"
            >Numéro WhatsApp (Réclamations)</label
          >
          <input
            class="inputComponent"
            v-model="form[code].whatsappPhoneNumberComplaint"
          />
        </div>

        <div>
          <label class="block mb-1 text-sm font-medium">Lien WhatsApp</label>
          <input class="inputComponent" v-model="form[code].whatsappLink" />
        </div>

        <div>
          <label class="block mb-1 text-sm font-medium"
            >Lien WhatsApp Chat</label
          >
          <input class="inputComponent" v-model="form[code].whatsappChatLink" />
        </div>

        <div>
          <label class="block mb-1 text-sm font-medium">Lien Telegram</label>
          <input class="inputComponent" v-model="form[code].telegramLink" />
        </div>
      </div>
    </div>
  </div>
</template>
